import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import { FiTarget } from 'react-icons/fi';

/**
 * Per-mentee scatter: x = applications in the last 30 days, y = interview
 * rate (%). Bubble size grows with the total recorded events for the
 * mentee, and the color flag is provided by the backend (thriving / active
 * / stalled / inactive). Clicking a bubble deep-links to the mentee detail.
 */
const FLAG_COLORS = {
  thriving: '#10b981',
  active: '#3b82f6',
  stalled: '#f59e0b',
  inactive: '#9ca3af',
};

const FLAG_LABELS = {
  thriving: 'Thriving',
  active: 'Active',
  stalled: 'Stalled',
  inactive: 'Inactive',
};

const ScatterTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm text-xs">
      <p className="font-semibold text-gray-800">{d.name}</p>
      <div className="mt-1 space-y-0.5 text-gray-600">
        <p>Apps last 30d: <span className="font-medium text-gray-800">{d.x}</span></p>
        <p>Interview rate: <span className="font-medium text-gray-800">{d.y}%</span></p>
        <p>Total applications: <span className="font-medium text-gray-800">{d.totalApplications}</span></p>
        <p className="mt-1">
          <span
            className="inline-block text-[10px] px-1.5 py-0.5 rounded-full"
            style={{
              backgroundColor: `${FLAG_COLORS[d.flag] || '#94a3b8'}20`,
              color: FLAG_COLORS[d.flag] || '#64748b',
            }}
          >
            {FLAG_LABELS[d.flag] || d.flag}
          </span>
        </p>
        <p className="text-[10px] text-gray-400 mt-1">Click to open mentee profile</p>
      </div>
    </div>
  );
};

const MenteeOutcomeMatrix = ({ items = [] }) => {
  const navigate = useNavigate();
  const safe = Array.isArray(items) ? items : [];

  // Add stable per-mentee jitter so identical (x,y) points don't fully overlap.
  // We bias the jitter by the menteeId hash so it doesn't flicker on
  // re-render. Maintain a separate `_size` derived from the event count.
  const data = useMemo(() => {
    return safe.map((d) => {
      const id = String(d.menteeId || '');
      const seed = id.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
      const jitterX = ((seed * 37) % 100) / 200 - 0.25;
      const jitterY = ((seed * 53) % 100) / 100 - 0.5;
      return {
        ...d,
        _x: (d.x ?? 0) + jitterX,
        _y: (d.y ?? 0) + jitterY,
      };
    });
  }, [safe]);

  const counts = useMemo(() => {
    const buckets = { thriving: 0, active: 0, stalled: 0, inactive: 0 };
    safe.forEach((d) => {
      buckets[d.flag] = (buckets[d.flag] || 0) + 1;
    });
    return buckets;
  }, [safe]);

  const maxSize = useMemo(
    () => safe.reduce((m, d) => Math.max(m, d.size || 0), 0),
    [safe]
  );

  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden p-5 border-t-4 border-amber-500 h-full flex flex-col">
      <div className="flex items-start justify-between mb-1">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Mentee Outcome Matrix</h3>
          <p className="text-xs text-gray-500">Effort (apps 30d) vs. interview rate per mentee</p>
        </div>
        <FiTarget className="h-4 w-4 text-amber-500" />
      </div>

      {data.length > 0 ? (
        <div style={{ height: 240 }} className="mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                type="number"
                dataKey="_x"
                name="Apps 30d"
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                label={{ value: 'Apps in last 30 days', position: 'bottom', fontSize: 10, fill: '#94a3b8', dy: 10 }}
              />
              <YAxis
                type="number"
                dataKey="_y"
                name="Interview rate"
                domain={[0, (dataMax) => Math.max(50, dataMax)]}
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                tickFormatter={(v) => `${v}%`}
                width={42}
              />
              <Tooltip content={<ScatterTooltip />} cursor={{ strokeDasharray: '3 3' }} />
              <ReferenceLine y={25} stroke="#e2e8f0" strokeDasharray="3 3" />
              <Scatter
                data={data}
                onClick={(e) => {
                  if (e?.menteeId) navigate(`/mentor/mentees/${e.menteeId}`);
                }}
              >
                {data.map((entry, i) => {
                  const fill = FLAG_COLORS[entry.flag] || '#94a3b8';
                  // Map raw event count to a radius between 5 and 14 — keeps
                  // tiny / huge mentees from dominating the plot visually.
                  const r = maxSize > 0
                    ? 5 + Math.min(9, (entry.size / maxSize) * 9)
                    : 6;
                  return (
                    <Cell
                      key={i}
                      fill={fill}
                      r={r}
                      fillOpacity={0.7}
                      stroke={fill}
                      strokeWidth={1}
                      style={{ cursor: 'pointer' }}
                    />
                  );
                })}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="py-10 text-center text-xs text-gray-400 flex-1 flex items-center justify-center">
          Mentees will appear here once they record activity.
        </div>
      )}

      <div className="flex flex-wrap gap-3 mt-2 mb-3 text-[10px] text-gray-500">
        {Object.entries(FLAG_LABELS).map(([key, label]) => (
          <span key={key} className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: FLAG_COLORS[key] }} />
            {label} ({counts[key] || 0})
          </span>
        ))}
        <span className="ml-2 text-gray-400">Bubble size = activity volume</span>
      </div>
    </div>
  );
};

export default MenteeOutcomeMatrix;
